import * as Phaser from 'phaser';
import type { Room } from 'colyseus.js';
import {
  COOKING_RECIPES,
  getCookingRecipe,
  type CookingRecipeDefinition,
  type CookingIngredient,
  ItemType,
  MSG_HOME_COOK,
  MSG_HOME_COOK_STARTED,
  MSG_HOME_COOK_COMPLETE,
  MSG_HOME_COOK_REJECTED,
  MSG_FOOL_DISCOVERY,
  MSG_INVENTORY_SYNC,
  type HomeCookRequest,
  type HomeCookStartedEvent,
  type HomeCookCompleteEvent,
  type HomeCookRejectedEvent,
  type FoolDiscoveryEvent,
  type InventorySyncEvent,
} from '@game/shared';
import { FontManager } from './FontManager';
import {
  HUD_BG,
  HUD_BORDER,
  HUD_TEXT_PRIMARY,
  HUD_TEXT_SECONDARY,
  PAL_GREEN,
  PAL_YELLOW,
  PAL_RED,
  hexToCSS,
} from './palette';

const PANEL_DEPTH = 1100;
const FADE_MS = 150;

const PANEL_W = 280;
const PANEL_PAD = 10;
const ROW_H = 40;
const TITLE_H = 24;
const DIVIDER_H = 2;
const BTN_H = 22;

const TOAST_FADE_MS = 600;
const TOAST_HOLD_MS = 3500;

const REJECTION_LABELS: Record<string, string> = {
  unknown_recipe: 'Unknown recipe.',
  insufficient_materials: 'Not enough materials.',
  already_cooking: 'Already cooking something.',
  not_home: 'You must be at your hearth.',
};

const INGREDIENT_NAMES: Partial<Record<ItemType, string>> = {
  [ItemType.MaterialWood]: 'Wood',
  [ItemType.MaterialStone]: 'Stone',
  [ItemType.MaterialThatch]: 'Thatch',
  [ItemType.MaterialCloth]: 'Cloth',
  [ItemType.MaterialInk]: 'Ink',
  [ItemType.MaterialWax]: 'Wax',
  [ItemType.MaterialAsh]: 'Ash',
  [ItemType.MaterialThawedWater]: 'Thawed Water',
  [ItemType.MaterialEmber]: 'Ember',
  [ItemType.MaterialSpiritsong]: 'Spiritsong',
};

interface RecipeRow {
  bg: Phaser.GameObjects.Rectangle;
  nameText: Phaser.GameObjects.Text;
  ingredientText: Phaser.GameObjects.Text;
  recipe: CookingRecipeDefinition;
}

export class CookingPanel {
  private scene: Phaser.Scene;
  private room: Room | null = null;
  private container: Phaser.GameObjects.Container;
  private visible = false;
  private rows: RecipeRow[] = [];
  private inventory = new Map<ItemType, number>();
  private cooking = false;
  private cookRecipeId: string | null = null;

  private cookArc: Phaser.GameObjects.Graphics | null = null;
  private cookArcTimer: Phaser.Time.TimerEvent | null = null;
  private cookStartTime = 0;
  private cookDurationMs = 0;

  private toastText: Phaser.GameObjects.Text | null = null;
  private toastTween: Phaser.Tweens.Tween | null = null;

  private unsubs: (() => void)[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setDepth(PANEL_DEPTH);
    this.container.setScrollFactor(0);
    this.container.setVisible(false);
    this.container.setAlpha(0);
    this.buildPanel();
  }

  attach(room: Room): void {
    this.room = room;

    this.unsubs.push(room.onMessage(MSG_INVENTORY_SYNC, (ev: InventorySyncEvent) => {
      this.inventory.clear();
      for (const item of ev.items) {
        const qty = Math.max(1, Math.floor(item.quantity ?? 1));
        this.inventory.set(item.itemType, (this.inventory.get(item.itemType) ?? 0) + qty);
      }
      if (this.visible) this.refreshRows();
    }));

    this.unsubs.push(room.onMessage(MSG_HOME_COOK_STARTED, (ev: HomeCookStartedEvent) => {
      this.cooking = true;
      this.cookRecipeId = ev.recipeId;
      this.cookStartTime = Date.now();
      this.cookDurationMs = ev.cookTimeMs;
      this.hide();
      this.startCookArc(ev.cookTimeMs);
    }));

    this.unsubs.push(room.onMessage(MSG_HOME_COOK_COMPLETE, (ev: HomeCookCompleteEvent) => {
      this.cooking = false;
      this.cookRecipeId = null;
      this.stopCookArc();
      const recipe = getCookingRecipe(ev.recipeId);
      const name = recipe?.name ?? 'food';
      this.showToast(`The ${name} is done. It smells of hearthstone.`);
    }));

    this.unsubs.push(room.onMessage(MSG_HOME_COOK_REJECTED, (ev: HomeCookRejectedEvent) => {
      this.cooking = false;
      this.cookRecipeId = null;
      this.stopCookArc();
      this.showToast(REJECTION_LABELS[ev.reason] ?? 'Cannot cook that.');
    }));

    this.unsubs.push(room.onMessage(MSG_FOOL_DISCOVERY, (ev: FoolDiscoveryEvent) => {
      this.cooking = false;
      this.cookRecipeId = null;
      this.stopCookArc();
      const d = ev.discovery;
      this.showToast(`${d.name} — ${d.flavorText}`);
    }));
  }

  toggle(): void {
    if (this.visible) this.hide(); else this.show();
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.refreshRows();
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: FADE_MS,
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: FADE_MS,
      onComplete: () => this.container.setVisible(false),
    });
  }

  isVisible(): boolean {
    return this.visible;
  }

  private buildPanel(): void {
    const recipes = COOKING_RECIPES.filter(r => !r.requiresGroupCook);
    const totalRows = recipes.length;
    const panelH = PANEL_PAD + TITLE_H + DIVIDER_H + totalRows * ROW_H + PANEL_PAD;

    const cx = 960 / 2;
    const cy = 540 / 2;
    const left = cx - PANEL_W / 2;
    const top = cy - panelH / 2;

    const bg = this.scene.add.rectangle(cx, cy, PANEL_W, panelH, HUD_BG, 0.93);
    bg.setStrokeStyle(1, HUD_BORDER);
    this.container.add(bg);

    const title = this.scene.add.text(
      cx, top + PANEL_PAD + TITLE_H / 2,
      'Hearth Cooking',
      FontManager.style('title', { color: hexToCSS(PAL_YELLOW), fontSize: '14px' }),
    );
    title.setOrigin(0.5, 0.5);
    this.container.add(title);

    const dividerY = top + PANEL_PAD + TITLE_H;
    const divider = this.scene.add.rectangle(cx, dividerY, PANEL_W - PANEL_PAD * 2, DIVIDER_H, HUD_BORDER);
    this.container.add(divider);

    let rowY = dividerY + DIVIDER_H + ROW_H / 2;
    for (const recipe of recipes) {
      const rowBg = this.scene.add.rectangle(cx, rowY, PANEL_W - PANEL_PAD * 2, ROW_H - 2, HUD_BG, 0);
      rowBg.setStrokeStyle(1, HUD_BORDER);
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on('pointerover', () => rowBg.setFillStyle(PAL_GREEN, 0.15));
      rowBg.on('pointerout', () => rowBg.setFillStyle(HUD_BG, 0));
      rowBg.on('pointerdown', () => this.onSelectRecipe(recipe));
      this.container.add(rowBg);

      const nameText = this.scene.add.text(
        left + PANEL_PAD + 4, rowY - 8,
        recipe.name,
        FontManager.style('hud', { color: hexToCSS(HUD_TEXT_PRIMARY) }),
      );
      nameText.setOrigin(0, 0.5);
      this.container.add(nameText);

      const ingStr = this.formatIngredients(recipe.ingredients);
      const ingredientText = this.scene.add.text(
        left + PANEL_PAD + 4, rowY + 8,
        ingStr,
        FontManager.style('hud-sm', { color: hexToCSS(HUD_TEXT_SECONDARY), fontSize: '8px' }),
      );
      ingredientText.setOrigin(0, 0.5);
      this.container.add(ingredientText);

      const timeText = this.scene.add.text(
        left + PANEL_W - PANEL_PAD - 4, rowY,
        `${recipe.cookTimeSeconds}s`,
        FontManager.style('hud-sm', { color: hexToCSS(HUD_TEXT_SECONDARY) }),
      );
      timeText.setOrigin(1, 0.5);
      this.container.add(timeText);

      this.rows.push({ bg: rowBg, nameText, ingredientText, recipe });
      rowY += ROW_H;
    }
  }

  private refreshRows(): void {
    for (const row of this.rows) {
      const canCook = this.hasIngredients(row.recipe.ingredients);
      const alpha = canCook ? 1 : 0.4;
      row.nameText.setAlpha(alpha);
      row.ingredientText.setAlpha(alpha);
      if (canCook) {
        row.bg.setInteractive({ useHandCursor: true });
      } else {
        row.bg.disableInteractive();
      }
      row.ingredientText.setText(this.formatIngredients(row.recipe.ingredients));
    }
  }

  private hasIngredients(ingredients: readonly CookingIngredient[]): boolean {
    for (const ing of ingredients) {
      if ((this.inventory.get(ing.itemType) ?? 0) < ing.quantity) return false;
    }
    return true;
  }

  private formatIngredients(ingredients: readonly CookingIngredient[]): string {
    return ingredients.map(ing => {
      const name = INGREDIENT_NAMES[ing.itemType] ?? ing.itemType;
      const have = this.inventory.get(ing.itemType) ?? 0;
      return `${name} ${have}/${ing.quantity}`;
    }).join('  ');
  }

  private onSelectRecipe(recipe: CookingRecipeDefinition): void {
    if (!this.room || this.cooking) return;
    if (!this.hasIngredients(recipe.ingredients)) return;
    const req: HomeCookRequest = { v: 1, recipeId: recipe.id };
    this.room.send(MSG_HOME_COOK, req);
  }

  // ── Cook progress arc ──

  private startCookArc(durationMs: number): void {
    this.stopCookArc();
    const hearthX = 960 / 2;
    const hearthY = 540 / 2 - 60;
    this.cookArc = this.scene.add.graphics();
    this.cookArc.setScrollFactor(0);
    this.cookArc.setDepth(PANEL_DEPTH - 1);

    const radius = 18;
    const thickness = 3;

    this.cookArcTimer = this.scene.time.addEvent({
      delay: 50,
      loop: true,
      callback: () => {
        if (!this.cookArc) return;
        const elapsed = Date.now() - this.cookStartTime;
        const pct = Math.min(1, elapsed / durationMs);
        this.cookArc.clear();
        this.cookArc.lineStyle(thickness, HUD_BORDER, 0.5);
        this.cookArc.strokeCircle(hearthX, hearthY, radius);
        this.cookArc.lineStyle(thickness, PAL_YELLOW, 1);
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + pct * Math.PI * 2;
        this.cookArc.beginPath();
        this.cookArc.arc(hearthX, hearthY, radius, startAngle, endAngle, false);
        this.cookArc.strokePath();
      },
    });
  }

  private stopCookArc(): void {
    if (this.cookArcTimer) {
      this.cookArcTimer.destroy();
      this.cookArcTimer = null;
    }
    if (this.cookArc) {
      this.cookArc.destroy();
      this.cookArc = null;
    }
  }

  // ── Toasts ──

  private showToast(message: string): void {
    if (this.toastTween) this.toastTween.destroy();
    if (this.toastText) this.toastText.destroy();

    const cx = this.scene.cameras.main.width / 2;
    const cy = this.scene.cameras.main.height - 50;

    this.toastText = this.scene.add.text(cx, cy, message, FontManager.style('body', {
      color: '#f5e6c8',
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true, stroke: true },
      wordWrap: { width: 400 },
      align: 'center',
    }));
    this.toastText.setOrigin(0.5, 0.5);
    this.toastText.setScrollFactor(0);
    this.toastText.setDepth(PANEL_DEPTH + 10);
    this.toastText.setAlpha(0);

    this.toastTween = this.scene.tweens.add({
      targets: this.toastText,
      alpha: { from: 0, to: 1 },
      duration: TOAST_FADE_MS,
      ease: 'Sine.easeIn',
      hold: TOAST_HOLD_MS,
      yoyo: true,
      onComplete: () => { this.toastText?.destroy(); this.toastText = null; },
    });
  }

  destroy(): void {
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    this.stopCookArc();
    if (this.toastTween) this.toastTween.destroy();
    if (this.toastText) this.toastText.destroy();
    this.container.destroy(true);
    this.rows.length = 0;
  }
}
